import React, { useEffect } from 'react';
import { VideoCameraIcon, BookOpenIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { ModelType } from '@packages/global';
import { useModelStore } from '../../stores/modelStore';
import { useCoinStore } from '../../stores/coinStore';
import { useTenantStore } from '../../stores/tenantStore';
import UserAssetSelector from '../projectAssets/UserAssetSelector';
import VideoGenerationTab from './components/VideoGenerationTab';
import ScriptIdeaTab from './components/ScriptIdeaTab';
import MvGenerationTab from './components/MvGenerationTab';
import { useVideoGeneration } from './hooks/useVideoGeneration';
import { useScriptIdea } from './hooks/useScriptIdea';
import { useMvGeneration } from './hooks/useMvGeneration';

const EPISODE_SCRIPT_GENERATION_PRICE = 20;
const SCRIPT_IDEA_PRICE = 5;

type TabType = 'video' | 'script' | 'mv';

interface CreateScriptSectionProps {
  projectId?: string;
  showScriptTab?: boolean;
}

export default function CreateScriptSection({ projectId, showScriptTab = false }: CreateScriptSectionProps) {
  const { t } = useTranslation(['script', 'common']);
  const { account } = useCoinStore();
  const { fetchModels, getModelsByType } = useModelStore();
  const { name: tenantName } = useTenantStore();
  const balance = account?.balance ?? 0;
  const studioName = tenantName || '';

  const [activeTab, setActiveTab] = React.useState<TabType>('video');
  const [showSubjectSelector, setShowSubjectSelector] = React.useState(false);

  const video = useVideoGeneration(projectId);
  const script = useScriptIdea();
  const mv = useMvGeneration(projectId);

  useEffect(() => {
    fetchModels().then(() => {
      const llm = getModelsByType(ModelType.LLM);
      const llmVl = getModelsByType(ModelType.LLM_VL);
      const t2i = getModelsByType(ModelType.TEXT_TO_IMAGE);
      const imgEdit = getModelsByType(ModelType.IMAGE_EDIT);
      const defaultModels = {
        llmModelId: llm[0]?.id || '',
        llmVlModelId: llmVl[0]?.id || '',
        text2imageModelId: t2i[0]?.id || '',
        imageEditModelId: imgEdit[0]?.id || '',
      };
      video.setSelectedModels(defaultModels);
      mv.setSelectedModels(defaultModels);
      script.setSelectedModels(defaultModels);
    });
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !video.loading && !script.loading) {
      e.preventDefault();
      if (activeTab === 'video') video.handleSubmit();
      else if (activeTab === 'script') script.handleSubmit();
    }
  };

  const tabs = [
    { key: 'video' as const, label: t('script:createScript.singleGeneration'), Icon: VideoCameraIcon },
    ...(showScriptTab ? [{ key: 'script' as const, label: t('script:createScript.multiGeneration'), Icon: BookOpenIcon }] : []),
    { key: 'mv' as const, label: t('script:createScript.mvGeneration'), Icon: MusicalNoteIcon },
  ];

  return (
    <div className="w-full max-w-5xl mx-auto pt-10 pb-4 px-4">
      <h2 className="text-2xl font-medium text-center text-gray-50 mb-8 tracking-wide">
        {t('script:createScript.welcome')}<span className="text-primary">{studioName}</span>！
      </h2>

      <div className="flex flex-col">
        {/* Tabs */}
        <div className="flex items-end">
          {tabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1 px-4 h-10 text-[13px] font-medium rounded-t-[10px] transition-colors ${activeTab === key
                ? 'bg-bg-surface text-primary border border-border-subtle border-b-0'
                : 'bg-bg-page text-gray-400 border border-border-subtle'
                }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Input Container */}
        <div className="bg-bg-surface p-4 border border-border-subtle rounded-tr-2xl rounded-b-2xl transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
          {activeTab === 'video' && (
            <VideoGenerationTab
              prompt={video.prompt}
              onPromptChange={video.setPrompt}
              loading={video.loading}
              uploadedFileName={video.uploadedFileName}
              uploading={video.uploading}
              fileInputRef={video.fileInputRef}
              onFileUpload={video.handleFileUpload}
              onClearFile={video.handleClearFile}
              selectedSubjects={video.selectedSubjects}
              onOpenSubjectSelector={() => setShowSubjectSelector(true)}
              onRemoveSubject={video.removeSubject}
              selectedStyle={video.selectedStyle}
              onStyleChange={video.setSelectedStyle}
              targetDuration={video.targetDuration}
              onTargetDurationChange={video.setTargetDuration}
              maxShotDuration={video.maxShotDuration}
              onMaxShotDurationChange={video.setMaxShotDuration}
              aspectRatio={video.aspectRatio}
              onAspectRatioChange={video.setAspectRatio}
              resolution={video.resolution}
              onResolutionChange={video.setResolution}
              selectedModels={video.selectedModels}
              onModelsChange={video.setSelectedModels}
              hasEnoughCoins={balance >= EPISODE_SCRIPT_GENERATION_PRICE}
              onSubmit={video.handleSubmit}
              onKeyPress={handleKeyPress}
            />
          )}

          {activeTab === 'script' && (
            <ScriptIdeaTab
              scriptContent={script.scriptContent}
              onScriptContentChange={script.setScriptContent}
              loading={script.loading}
              novelFileName={script.novelFileName}
              novelUploading={script.novelUploading}
              novelFileInputRef={script.novelFileInputRef}
              onNovelFileUpload={script.handleNovelFileUpload}
              onClearNovelFile={script.handleClearNovelFile}
              selectedModels={script.selectedModels}
              onModelsChange={script.setSelectedModels}
              selectedSubjects={script.selectedSubjects}
              onOpenSubjectSelector={() => setShowSubjectSelector(true)}
              onRemoveSubject={script.removeSubject}
              selectedStyle={script.selectedStyle}
              onStyleChange={script.setSelectedStyle}
              aspectRatio={script.aspectRatio}
              onAspectRatioChange={script.setAspectRatio}
              resolution={script.resolution}
              onResolutionChange={script.setResolution}
              maxShotDuration={script.maxShotDuration}
              onMaxShotDurationChange={script.setMaxShotDuration}
              hasEnoughCoins={balance >= SCRIPT_IDEA_PRICE}
              canSubmit={script.canSubmit}
              onSubmit={script.handleSubmit}
              onKeyPress={handleKeyPress}
            />
          )}

          {activeTab === 'mv' && (
            <MvGenerationTab
              audioFileName={mv.audioFileName}
              uploading={mv.uploading}
              audioInputRef={mv.audioInputRef}
              onAudioUpload={mv.handleAudioUpload}
              onClearAudio={mv.handleClearAudio}
              onSelectAudioFromMusic={mv.setAudioFromMusic}
              onSubmit={mv.handleSubmit}
              canSubmit={mv.canSubmit}
              hasEnoughCoins={balance >= EPISODE_SCRIPT_GENERATION_PRICE}
              prompt={mv.prompt}
              onPromptChange={mv.setPrompt}
              selectedSubjects={mv.selectedSubjects}
              onOpenSubjectSelector={() => setShowSubjectSelector(true)}
              onRemoveSubject={mv.removeSubject}
              selectedStyle={mv.selectedStyle}
              onStyleChange={mv.setSelectedStyle}
              aspectRatio={mv.aspectRatio}
              onAspectRatioChange={mv.setAspectRatio}
              resolution={mv.resolution}
              onResolutionChange={mv.setResolution}
              selectedModels={mv.selectedModels}
              onModelsChange={mv.setSelectedModels}
              maxShotDuration={mv.maxShotDuration}
              onMaxShotDurationChange={mv.setMaxShotDuration}
            />
          )}
        </div>
      </div>

      <UserAssetSelector
        isOpen={showSubjectSelector}
        projectId={projectId}
        onConfirm={activeTab === 'mv' ? mv.addSubjects : activeTab === 'script' ? script.addSubjects : video.addSubjects}
        onClose={() => setShowSubjectSelector(false)}
      />
    </div>
  );
}
